import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Checkbox, Col, DatePicker, Input, Row } from "antd";
import TextArea from "antd/es/input/TextArea";
import React, { useEffect } from "react";
import { useFieldArray, useForm, Controller, useFormContext } from "react-hook-form";
import * as yup from "yup";
import dayjs from "dayjs";
import { useAxiosPrivate } from "hooks/useAxiosPrivate";
import { responsive } from "antd-style";
import { useNavigate } from "react-router-dom";
import { showNotification } from "service/Notification";

const Collection = ({ ocf, ocfId, getOcfDetails }) => {
  /* ✅ Schema */
  const schema = yup.object({
    futurePaymentsForm: yup.object({
      payments: yup.array().of(
        yup.object({
          expectedDate: yup.string(),
          percentage: yup.number(),
          billingAllowed: yup.boolean(),
          actualDate: yup.string().nullable(),
        })
      ),

      remark: yup.string().required("Remark required"),

      billingDone: yup.boolean(),

      billingDoneDate: yup.string().when("billingDone", {
        is: true,
        then: () => yup.string().required("Billing Done Date required"),
      }),

      collected: yup.boolean(),

      collectedDate: yup.string().when("collected", {
        is: true,
        then: () => yup.string().required("Collection Done Date required"),
      }),
    }),
  });
  const { watchContext } = useFormContext();
  /* ✅ useForm */
  const {
    control,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      futurePaymentsForm: {
        payments: [],
        remark: "",
        billingDone: false,
        billingDoneDate: "",
        collected: false,
        collectionDoneDate: "",
      },
    },
  });

  const billingDone = watch("futurePaymentsForm.billingDone");
  const billingDate = watch("futurePaymentsForm.billingDoneDate");
  const isBillingLocked = !!ocf?.accountDetails?.billedDate;
  const isCollectionLocked = !!ocf?.accountDetails?.collectedDate;
  const collectionDone = watch("futurePaymentsForm.collected");
  const collectionDate = watch("futurePaymentsForm.collectedDate");

  /* ✅ FieldArray */
  const { fields } = useFieldArray({
    control,
    name: "futurePaymentsForm.payments",
  });

  /* ✅ Load API */
  useEffect(() => {
    const paymentDetails = ocf?.paymentDetails ?? [];
    setValue("futurePaymentsForm.payments", paymentDetails, {
      shouldValidate: true,
    });
    setValue("futurePaymentsForm.billingDone", ocf?.accountDetails?.billed);
    setValue("futurePaymentsForm.billingDoneDate", ocf?.accountDetails?.billedDate);
    setValue("futurePaymentsForm.collected", ocf?.accountDetails?.collected);
    setValue("futurePaymentsForm.collectedDate", ocf?.accountDetails?.collectedDate);
  }, [ocf]);

  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();

  const onSubmit = async (data) => {
    const payload = {
      ocfId,
      // futurePayments: data.futurePaymentsForm.payments,
      accountsRemark: `${ocf?.accountsRemark || ""}\n${
        data.futurePaymentsForm.remark
      } ( AddedOn: ${dayjs().format("DD-MM-YYYY HH:mm:ss")})`,
      accountDetails: {
        billed: data.futurePaymentsForm.billingDone,
        billedDate: data.futurePaymentsForm.billingDoneDate,
        collected: data.futurePaymentsForm.collected,
        collectedDate: data.futurePaymentsForm.collectedDate,
      },
    };
    await axiosPrivate({ method: "POST", url: "om/payment?stage=4", data: payload })
      .then((resp) => {
        if (resp.status == 200) {
          navigate(0);
        } else {
          showNotification("error", "Error", "Some error occured!");
        }
      })
      .catch((err) => {
        showNotification("error", "Error", "Some error occured!");
        console.log(err);
      });
  };

  const isFormDisabled = (status) => {
    return ["Approved", "Confirmed", "Completed", "Billed", "Collected"].includes(status);
  };

  return (
    // <ConfigProvider componentDisabled={isFormDisabled(ocf?.status)}>
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(onSubmit)(e);
      }}
    >
      <Row gutter={[16, 16]} style={{ marginTop: 20, display: "flex", alignItems: "center" }}>
        {/* PAYMENT ROWS */}
        {fields.map((field, index) => (
          <React.Fragment key={field.id}>
            <Col span={6}>
              <label>Expected Date</label>
              <Controller
                name={`futurePaymentsForm.payments.${index}.expectedDate`}
                control={control}
                render={({ field }) => <Input {...field} disabled />}
              />
            </Col>

            <Col span={6}>
              <label>Payment Date</label>

              <Controller
                name={`futurePaymentsForm.payments.${index}.actualDate`}
                control={control}
                render={({ field }) => (
                  <>
                    <DatePicker
                      disabled
                      style={{ width: "100%" }}
                      format="DD-MM-YYYY"
                      value={field.value ? dayjs(field.value, "DD-MM-YYYY") : null}
                      onChange={(date) => field.onChange(date ? date.format("DD-MM-YYYY") : null)}
                    />

                    <p style={{ color: "red", fontSize: 12 }}>
                      {errors?.futurePaymentsForm?.payments?.[index]?.paymentDate?.message}
                    </p>
                  </>
                )}
              />
            </Col>

            <Col span={4}>
              <label> Builder Payment %</label>
              <Controller
                name={`futurePaymentsForm.payments.${index}.percentage`}
                control={control}
                render={({ field }) => <Input {...field} disabled />}
              />
            </Col>

            <Col span={4}>
              <Controller
                name={`futurePaymentsForm.payments.${index}.billingAllowed`}
                control={control}
                render={({ field }) => (
                  <Checkbox checked={field.value} disabled>
                    Billing Allowed
                  </Checkbox>
                )}
              />
            </Col>
          </React.Fragment>
        ))}

        {/* BILLING DONE */}
        <Col span={6}>
          <Controller
            name="futurePaymentsForm.billingDone"
            control={control}
            render={({ field }) => (
              <Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)}>
                Billing
              </Checkbox>
            )}
          />
        </Col>

        <Col span={6}>
          <label>Billing Date</label>

          <Controller
            name="futurePaymentsForm.billingDoneDate"
            control={control}
            render={({ field }) => (
              <>
                <DatePicker
                  disabled={!billingDone || isBillingLocked}
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(d) => field.onChange(d ? d.format("DD-MM-YYYY") : "")}
                />

                <p style={{ color: "red", fontSize: 12 }}>
                  {errors?.futurePaymentsForm?.billingDoneDate?.message}
                </p>
              </>
            )}
          />
        </Col>

        <Col span={24}></Col>

        {/* COLLECTION DONE */}
        <Col span={6}>
          <Controller
            name="futurePaymentsForm.collected"
            control={control}
            render={({ field }) => (
              <Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)}>
                Collection
              </Checkbox>
            )}
          />
        </Col>

        <Col span={6}>
          <label>Collection Date</label>

          <Controller
            name="futurePaymentsForm.collectedDate"
            control={control}
            render={({ field }) => (
              <>
                <DatePicker
                  disabled={!collectionDone || isCollectionLocked}
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(d) => field.onChange(d ? d.format("DD-MM-YYYY") : "")}
                />

                <p style={{ color: "red", fontSize: 12 }}>
                  {errors?.futurePaymentsForm?.collectedDate?.message}
                </p>
              </>
            )}
          />
        </Col>

        <Col span={24}></Col>

        {/* REMARK */}
      </Row>
      <Row>
        {ocf?.accountsRemark && (
          <label style={{ whiteSpace: "pre-line", marginBottom: "12px" }}>
            {ocf?.accountsRemark}
          </label>
        )}
      </Row>
      <Col span={12}>
        <label>Remark</label>

        <Controller
          name="futurePaymentsForm.remark"
          control={control}
          render={({ field }) => (
            <>
              <TextArea {...field} rows={3} />

              <p style={{ color: "red", fontSize: 12 }}>
                {errors?.futurePaymentsForm?.remark?.message}
              </p>
            </>
          )}
        />
      </Col>

      <Col span={24}>
        <Button type="primary" htmlType="button" onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </Col>
    </form>
    // </ConfigProvider>
  );
};

export default Collection;
